import { clusterBadgeClasses, clusterKeyForOpportunity } from '@/config/market-config'
import type { VaultMarket } from '@/features/evault-details/data/products'
import type { YieldSource } from '@/features/unified-earn/types'
import type { BorrowSource } from '../types'

const UNLABELED_SHORT = 'Unlabeled'
const UNLABELED_FULL = 'HypurrFi Unlabeled'
// Matches the `/15` opacity convention used by every cluster tile's `background`,
// since `sourceClasses` composes its output from `CLUSTER_CONFIG.{background,foreground}`.
const UNLABELED_CLASSES = 'bg-slate-400/15 text-slate-400'

/**
 * Short, data-driven label for an elend market cluster derived from
 * `market.name`. New clusters populate automatically — no code change needed.
 *
 * "HypurrFi Prime" → "Prime"
 * "HypurrFi Scale" → "Scale"
 * "HypurrFi Earn Vault" → "Earn"
 * missing / unknown → "Unlabeled"
 */
export function shortMarketLabel(market?: VaultMarket): string {
  if (!market?.name) return UNLABELED_SHORT
  return market.name
    .replace(/^HypurrFi\s+/i, '')
    .replace(/\s+Vault$/i, '')
    .trim()
}

/**
 * Resolve the display label for a source. For elend vaults, the cluster name
 * comes from `market.name` so new markets (e.g. Scale) appear without a code
 * change. Vaults with no market metadata render as "HypurrFi Unlabeled".
 */
export function sourceLabel(source: BorrowSource | YieldSource, market?: VaultMarket): string {
  if (source === 'elend') {
    return market?.name ?? UNLABELED_FULL
  }
  switch (source) {
    case 'pooled':
      return 'Pooled'
    case 'isolated':
      return 'Isolated'
    case 'usdxl':
      return 'USDXL'
    default:
      return source
  }
}

// Stable hash-palette fallback — only used when an elend opportunity's
// `market.key` doesn't map to any registered cluster (e.g. indexer returned a
// cluster not yet in `HYPEREVM_PRODUCTS`). Known clusters get their badge
// classes from `CLUSTER_CONFIG.{background,foreground}` via
// `clusterBadgeClasses()`, matching the Markets Overview tile colors.
// Classes are written as full literals so the Tailwind JIT picks them up.
const ELEND_FALLBACK_PALETTE = [
  'bg-pink-400/15 text-pink-400',
  'bg-orange-400/15 text-orange-400',
  'bg-teal-400/15 text-teal-400',
  'bg-fuchsia-400/15 text-fuchsia-400',
]

function hashString(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0
  return Math.abs(h)
}

export function sourceClasses(source: BorrowSource | YieldSource, market?: VaultMarket): string {
  // USDXL isn't a cluster — it's a borrow source specific to the Opportunities page.
  if (source === 'usdxl') return 'bg-gradient-yellow/15 text-gradient-yellow'

  // Known cluster → reuse the same `background` + `foreground` classes as the
  // Markets Overview tile so colors stay consistent across surfaces.
  const clusterKey = clusterKeyForOpportunity({ source, market })
  if (clusterKey) return clusterBadgeClasses(clusterKey)

  // Unknown elend cluster (e.g. indexer-only, not yet in HYPEREVM_PRODUCTS) →
  // stable hash-picked color so the row is still distinguishable.
  if (source === 'elend' && market?.key) {
    return ELEND_FALLBACK_PALETTE[hashString(market.key) % ELEND_FALLBACK_PALETTE.length] ?? UNLABELED_CLASSES
  }

  return UNLABELED_CLASSES
}

export function formatCompactUsd(value: number): string {
  if (!Number.isFinite(value)) return '-'
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  if (value >= 1) return `$${value.toFixed(0)}`
  return '<$1'
}

/** Safely format a rate (0-100 scale) with the given decimal places. */
export function formatRate(value: number, decimals = 2): string {
  if (!Number.isFinite(value)) return '0.00'
  return value.toFixed(decimals)
}
